/**
 * Questionnaire Service (T048-T049)
 * Handles API calls for questionnaire operations
 */

import axios, { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

// Type definitions
export interface Question {
  id: number;
  order: number;
  text: string;
  question_type: string;
  options: any;
  is_required: boolean;
}

export interface Questionnaire {
  id: number;
  title: string;
  description: string;
  category: string;
  questions: Question[];
  question_count: number;
  is_active: boolean;
}

export interface QuestionnaireResponse {
  id: number;
  questionnaire: number;
  conversation: number;
  answers: Record<string, string>;
  profile_status: string;
  created_at: string;
}

export interface Profile {
  status?: string;
  message?: string;
  profile_data?: Record<string, unknown>;
  specialist_recommendations?: Array<{
    specialist_id: number;
    name: string;
    specialization: string;
    match_score: number;
  }>;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const questionnaireService = {
  async getQuestionnaires(): Promise<Questionnaire[]> {
    const response = await axios.get(
      `${API_BASE_URL}/questionnaires/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async getQuestionnaire(questionnaireId: number): Promise<Questionnaire> {
    const response = await axios.get(
      `${API_BASE_URL}/questionnaires/${questionnaireId}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async submitResponse(
    questionnaireId: number,
    conversationId: number,
    answers: Record<string, string>
  ): Promise<QuestionnaireResponse> {
    const response = await axios.post(
      `${API_BASE_URL}/questionnaires/responses/`,
      {
        questionnaire_id: questionnaireId,
        conversation_id: conversationId,
        answers: answers
      },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async getResponseStatus(responseId: number): Promise<QuestionnaireResponse> {
    const response = await axios.get(
      `${API_BASE_URL}/questionnaires/responses/${responseId}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async getQuestionnaireProfile(responseId: number): Promise<Profile> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/questionnaires/responses/${responseId}/profile/`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      if (axiosError.response && axiosError.response.status === 404) {
        return {
          status: 'processing',
          message: axiosError.response.data?.message || 'Profile generation in progress'
        };
      }
      throw error;
    }
  },

  async pollForProfile(
    responseId: number,
    maxAttempts: number = 15,
    intervalMs: number = 2000
  ): Promise<Profile> {
    let attempts = 0;

    const poll = async (): Promise<Profile> => {
      attempts++;
      const profile = await this.getQuestionnaireProfile(responseId);

      if (profile.status !== 'processing') {
        return profile;
      }

      if (attempts >= maxAttempts) {
        throw new Error('Profile generation timed out');
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      return poll();
    };

    return poll();
  },

  async suggestCategory(conversationId: number): Promise<{ category: string; confidence: number }> {
    const response = await axios.post(
      `${API_BASE_URL}/questionnaires/suggest-category/`,
      { conversation_id: conversationId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};
